import PageLayout from 'components/pages/PageLayout'
import React from 'react'
import { NextPage } from 'next'
import { withApollo } from 'components/withApollo'
import PageMeta from 'components/PageMeta'
import PageError from 'components/pages/PageError'
import groupBy from 'lodash/groupBy'
import map from 'lodash/map'
import sortBy from 'lodash/sortBy'
import MarketList, {
  GroupedMarketsType
} from 'components/pages/TrucksNearbyPage/MarketList'
import FoodTypesSection from 'components/FoodTypesSection'
import CtaSection from 'components/pages/BlogPostPage/CtaSection'
import NearbyMarketsCards from 'components/pages/TrucksNearbyPage/NearbyMarketsCards'
import LoadingPage from 'components/pages/LoadingPage'
import TrucksMap from 'components/pages/MarketPage/TrucksMap'

import USA_IMAGE from './usa.svg'
import NearbyFaq from './NearbyFaq'
import { useMarketsNearMeQuery } from './types/marketsNearMe'

function convertToColumns(states: GroupedMarketsType[], itemsInColumn: number) {
  const columns: GroupedMarketsType[][] = []
  let currentGroup: GroupedMarketsType[] = []
  states.forEach((state, index) => {
    if (currentGroup.length === itemsInColumn) {
      columns.push(currentGroup)
      currentGroup = []
    }
    const isLast = index === states.length - 1
    if (isLast) {
      columns.push(currentGroup)
    }
    currentGroup.push({
      state: state.state,
      markets: state.markets
    })
  })
  return columns
}

const TrucksNearbyPage: NextPage = () => {
  const { data, error } = useMarketsNearMeQuery({
    fetchPolicy: 'cache-first',
    variables: {
      limit: 3
    }
  })

  const meta = (
    <PageMeta
      canonicalPath="/food-trucks-near-me"
      description="Curated food trucks near you. Order from food trucks online or hire a food truck for a catering event."
      keywords="search, map, states, food trucks"
      ldJson={data?.structuredData?.foodTrucksNearMePage}
      title="Best Food Trucks | Food Trucks Near Me"
    />
  )

  if (!data?.markets || !data?.foodTypePages) {
    return <LoadingPage meta={meta} staticPage={false} />
  }
  if (error) return <PageError>{error.message}</PageError>

  const groupedMarkets: GroupedMarketsType[] = map(
    groupBy(data?.markets.records, (m) => m.stateFull),
    (markets, state) => ({
      state,
      markets
    })
  )

  const sortedMarkets = sortBy(groupedMarkets, (m) => m.state)
  const columnsDesktop = convertToColumns(
    sortedMarkets,
    Math.ceil(groupedMarkets.length / 6)
  )
  const columnsMobile = convertToColumns(
    sortedMarkets,
    Math.ceil(groupedMarkets.length / 2)
  )

  const nearestMarket = data?.nearbyMarkets?.records[0]

  return (
    <PageLayout staticPage={false} fullWidth noWidth>
      {meta}
      <div className="scroll-smooth">
        <div className="relative">
          <div className="relative px-4 py-8 sm:px-6 sm:py-10 lg:px-10">
            <h1 className="text-center text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
              <span className="">Find Food Trucks</span>{' '}
              <span className="text-orange-700">near me</span>
            </h1>
            <p className="mt-6 max-w-lg mx-auto text-center text-xl sm:max-w-4xl font-medium">
              Curated food trucks near you. Order from food trucks online or
              hire a food truck for a catering event.
            </p>
          </div>
        </div>

        <div className="px-4 max-w-7xl mx-auto">
          <NearbyMarketsCards markets={data?.nearbyMarkets?.records || []} />
          {nearestMarket && (
            <div className="mt-4">
              <TrucksMap
                defaultCenter={{
                  lat: nearestMarket.latitude,
                  lng: nearestMarket.longitude
                }}
                market={nearestMarket}
                showTabs={false}
                showTitle={false}
                showInNewPage
              />
            </div>
          )}

          <div className="relative md:pt-5 my-10 mx-4 md:mx-0 bg-contain bg-no-repeat bg-top">
            <div
              className="-z-10 absolute inset-0 bg-contain bg-no-repeat bg-top"
              style={{
                backgroundImage: `url(${USA_IMAGE.src})`,
                height: '600px'
              }}
            />
            <h2 className="scroll-offset scroll-smooth text-center text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl">
              Find trucks by state
            </h2>
            <div className="bg-top pt-5 md:pt-10">
              <div className="md:hidden block">
                <MarketList columns={columnsMobile} />
              </div>
              <div className="hidden md:block">
                <MarketList columns={columnsDesktop} />
              </div>
            </div>
          </div>
        </div>

        <CtaSection />

        <div className="px-4 max-w-7xl mx-auto">
          <FoodTypesSection
            foodTypePages={data?.foodTypePages}
            title="Food truck cuisines"
          />
          <NearbyFaq markets={data?.nearbyMarkets?.records ?? null} />
        </div>
      </div>
    </PageLayout>
  )
}

export default withApollo({ ssr: true })(TrucksNearbyPage as NextPage)
